import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, from, map, Observable, switchMap, tap } from 'rxjs';

import { AvailableLanguagesEnum } from '../constants/available-languages.enum';

@Injectable({
  providedIn: 'root',
})
export class TouchTranslateService {
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly dateAdapter = inject(DateAdapter, { optional: true });

  initLanguage(): Observable<void> {
    const defaultLang = this.normalizeLanguage(this.translate.getBrowserLang());
    this.translate.setFallbackLang(AvailableLanguagesEnum.EN);

    return this.changeUserLanguage(defaultLang);
  }

  updateUserLanguage(language: AvailableLanguagesEnum): void {
    void firstValueFrom(this.changeUserLanguage(language));
  }

  changeUserLanguage(localeId: AvailableLanguagesEnum | string | null | undefined): Observable<void> {
    const language = this.normalizeLanguage(localeId);

    return from(this.getLocale()[language]()).pipe(
      tap(module => {
        registerLocaleData(module.default);
        this.applyRuntimeLocale(language);
      }),
      switchMap(() => this.translate.use(language)),
      map(() => void 0),
    );
  }

  private normalizeLanguage(localeId: string | null | undefined): AvailableLanguagesEnum {
    return Object.values(AvailableLanguagesEnum).includes(localeId as AvailableLanguagesEnum)
      ? (localeId as AvailableLanguagesEnum)
      : AvailableLanguagesEnum.EN;
  }

  private applyRuntimeLocale(language: AvailableLanguagesEnum): void {
    this.document.documentElement.lang = language;
    this.document.documentElement.dir = language === AvailableLanguagesEnum.AR ? 'rtl' : 'ltr';
    this.dateAdapter?.setLocale(language);
  }

  private getLocale() {
    return {
      [AvailableLanguagesEnum.FR]: () => import('@angular/common/locales/fr'),
      [AvailableLanguagesEnum.AR]: () => import('@angular/common/locales/ar'),
      [AvailableLanguagesEnum.ES]: () => import('@angular/common/locales/es'),
      [AvailableLanguagesEnum.IT]: () => import('@angular/common/locales/it'),
      [AvailableLanguagesEnum.EN]: () => import('@angular/common/locales/en'),
    };
  }
}
